import React from "react";
import { Button, Select, Input, InputNumber, Radio, Checkbox } from "antd";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const blankScheduleEntry = () => ({
  assetType: "Post",
  assetTypeCustom: "",
  frequencyUnit: "week",
  frequencyCount: 1,
  frequencyCustom: "",
  daysMode: "specific",
  days: [],
  daysCustom: "",
});

// Older records may predate some fields, so fill the gaps before editing.
const toFormEntry = (e) => ({ ...blankScheduleEntry(), ...(e || {}) });

/**
 * Controlled structured editor for `postingSchedule`, shaped to drop straight
 * into an antd <Form.Item> (reads `value`, calls `onChange`).
 *
 * Field shape mirrors the onboarding editor exactly: `postingSchedule` is the
 * source of truth for engagement RAG adherence, so anything written here has to
 * match what the backend normalizer expects. The old free-text tag list edited
 * only the derived `schedule` display strings, which silently left adherence
 * running on the stale cadence.
 */
const PostingScheduleField = ({ value, onChange }) => {
  const entries = value?.length ? value.map(toFormEntry) : [blankScheduleEntry()];

  const updateEntry = (idx, patch) =>
    onChange?.(entries.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  const removeEntry = (idx) =>
    onChange?.(entries.length > 1 ? entries.filter((_, i) => i !== idx) : entries);
  const addEntry = () => onChange?.([...entries, blankScheduleEntry()]);

  const labelStyle = { fontSize: 11, color: "#888", width: 92, flexShrink: 0, paddingTop: 6 };
  const rowStyle = { display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 };

  return (
    <div>
      {entries.map((entry, idx) => (
        <div
          key={idx}
          style={{
            border: "1px solid #e8e8e8",
            borderRadius: 8,
            padding: "12px 14px",
            marginBottom: 10,
            background: "#fff",
            position: "relative",
          }}
        >
          {entries.length > 1 && (
            <Button
              type="text"
              size="small"
              onClick={() => removeEntry(idx)}
              style={{ position: "absolute", top: 6, right: 6, color: "#c0392b", fontSize: 12 }}
            >
              Remove
            </Button>
          )}

          {/* Asset Type */}
          <div style={rowStyle}>
            <span style={labelStyle}>Asset Type</span>
            <div style={{ display: "flex", gap: 8, flex: 1, flexWrap: "wrap" }}>
              <Select
                size="small"
                style={{ width: 130 }}
                value={entry.assetType}
                onChange={(v) => updateEntry(idx, { assetType: v })}
                options={[
                  { label: "Post", value: "Post" },
                  { label: "Blog", value: "Blog" },
                  { label: "Custom", value: "Custom" },
                ]}
              />
              {entry.assetType === "Custom" && (
                <Input
                  size="small"
                  style={{ flex: 1, minWidth: 140 }}
                  placeholder="e.g. Newsletter, Reel"
                  value={entry.assetTypeCustom}
                  onChange={(e) => updateEntry(idx, { assetTypeCustom: e.target.value })}
                />
              )}
            </div>
          </div>

          {/* Frequency */}
          <div style={rowStyle}>
            <span style={labelStyle}>Frequency</span>
            <div style={{ display: "flex", gap: 8, flex: 1, alignItems: "center", flexWrap: "wrap" }}>
              {entry.frequencyUnit === "custom" ? (
                <Input
                  size="small"
                  style={{ width: 150 }}
                  placeholder="e.g. every alternate day"
                  value={entry.frequencyCustom}
                  onChange={(e) => updateEntry(idx, { frequencyCustom: e.target.value })}
                />
              ) : (
                <InputNumber
                  size="small"
                  min={1}
                  style={{ width: 70 }}
                  value={entry.frequencyCount}
                  onChange={(v) => updateEntry(idx, { frequencyCount: v })}
                />
              )}
              <Select
                size="small"
                style={{ width: 120 }}
                value={entry.frequencyUnit}
                onChange={(v) => updateEntry(idx, { frequencyUnit: v })}
                options={[
                  { label: "per week", value: "week" },
                  { label: "per month", value: "month" },
                  { label: "Custom", value: "custom" },
                ]}
              />
            </div>
          </div>

          {/* Posting Days */}
          <div style={rowStyle}>
            <span style={labelStyle}>Posting Days</span>
            <div style={{ flex: 1 }}>
              <Radio.Group
                size="small"
                value={entry.daysMode}
                onChange={(e) => updateEntry(idx, { daysMode: e.target.value })}
                style={{ marginBottom: 8 }}
              >
                <Radio.Button value="specific">Specific days</Radio.Button>
                <Radio.Button value="custom">Custom / Anytime</Radio.Button>
              </Radio.Group>
              {entry.daysMode === "specific" ? (
                <Checkbox.Group
                  value={entry.days}
                  onChange={(v) => updateEntry(idx, { days: v })}
                  style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                  options={WEEKDAYS.map((d) => ({ label: d, value: d }))}
                />
              ) : (
                <Input
                  size="small"
                  style={{ width: "100%" }}
                  placeholder='e.g. "anytime"'
                  value={entry.daysCustom}
                  onChange={(e) => updateEntry(idx, { daysCustom: e.target.value })}
                />
              )}
            </div>
          </div>
        </div>
      ))}

      <Button size="small" onClick={addEntry} style={{ fontSize: 12 }}>
        + Add asset type
      </Button>
    </div>
  );
};

// A cadence only counts toward adherence if it has a usable asset type, so
// reject entries the backend normalizer would silently drop.
export const hasUsableEntry = (entries) =>
  Array.isArray(entries) &&
  entries.some((e) => e?.assetType === "Post" || e?.assetType === "Blog" || (e?.assetType === "Custom" && e?.assetTypeCustom?.trim()));

export default PostingScheduleField;
